var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { AsciiText } from './framework/AsciiText';
import { CommandSocketInitializer } from './framework/CommandSocketInitializer';
import { Timeouts } from './framework/Timeouts';
const debug = false;
export class NecMultisync {
    constructor(socket, state, log, framework) {
        this.socket = socket;
        this.state = state;
        this.log = log;
        this.framework = framework;
        // other class properties and constructor
        this.deviceId = 0x2a; // Broadcasts to all linked displays
        this.initializerSteps = [
            {
                errorCode: 'verifyFailed',
                send: `\x01\x30\x2A\x30\x41\x30\x36\x02\x30\x31\x44\x36\x03\x1F`,
                next: /\x01\x30[\s\S]{5}\x02[\s\S]{15}([\s\S])\x03|EDEN PM/i,
            },
        ];
        this.timeouts = new Timeouts();
        this.busyTimeout = this.timeouts.create();
        // controls
        this.busy = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.busy.value;
            }),
        };
        // Note that the checksum can equal \r which is our receive command terminator, so we don't
        // include the checksum character in the match regex's.
        this.displayBlank = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                // screen mute in the NEC API manual
                const match = yield this.socket.send(this.makeCommand([
                    0x01,
                    0x30,
                    this.deviceId,
                    0x30,
                    0x43,
                    0x30,
                    0x36,
                    0x02,
                    0x31,
                    0x30,
                    0x42,
                    0x36,
                    0x03, // ETX end of message
                ]), /\x01\x30[\s\S]{5}\x02[\s\S]{2}[\s\S]{2}\x42\x36[\s\S]{8}[\s\S]([\s\S])\x03/i, tags);
                return match[1] === '\x31';
            }),
            set: (blank, tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(this.makeCommand([
                    0x01,
                    0x30,
                    this.deviceId,
                    0x30,
                    0x45,
                    0x30,
                    0x41,
                    0x02,
                    0x31,
                    0x30,
                    0x42,
                    0x36,
                    0x30,
                    0x30,
                    0x30,
                    blank ? 0x31 : 0x32,
                    0x03, // ETX end transmission
                ]), /\x01\x30[\s\S]{5}\x02[\s\S]{2}[\s\S]{2}\x42\x36[\s\S]{8}[\s\S]([\s\S])\x03/, tags);
                return match[1] === '\x31';
            }),
            hasControl: () => __awaiter(this, void 0, void 0, function* () {
                try {
                    const match = yield this.socket.send(this.makeCommand([
                        0x01,
                        0x30,
                        this.deviceId,
                        0x30,
                        0x43,
                        0x30,
                        0x36,
                        0x02,
                        0x31,
                        0x30,
                        0x42,
                        0x36,
                        0x03, // ETX end of message
                    ]), /\x01\x30[\s\S]{5}\x02([\s\S]{2})[\s\S]{2}\x42\x36[\s\S]{8}[\s\S][\s\S]\x03/i, ['get.displayBlank', 'hasControl']);
                    return match[1] === '\x30\x30';
                }
                catch (error) {
                    if (error.code !== 'sendTimeout')
                        throw error;
                    return false;
                }
            }),
            powerOffRestrictions: ['get', 'hasControl', 'set'],
        };
        this.firmwareVersion = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.firmwareVersion.value;
            }),
            hasControl: () => __awaiter(this, void 0, void 0, function* () {
                try {
                    const match = yield this.socket.send(this.makeCommand([
                        0x01,
                        0x30,
                        this.deviceId,
                        0x30,
                        0x41,
                        0x30,
                        0x38,
                        0x02,
                        0x43,
                        0x41,
                        0x30,
                        0x32,
                        0x30,
                        0x30,
                        0x03,
                    ]), /\x01\x30[\s\S]{5}\x02[\s\S]{8}([\s\S]{14})\x03|\x01\x30\x30\x41\x41\x30\x34\x02[\s\S]{2}\x03/i, ['get.firmwareVersion', 'hasControl']);
                    if (match[1]) {
                        this.state.firmwareVersion.update(this.parseFirmwareVersion(match[1]));
                        return true;
                    }
                    return false;
                }
                catch (error) {
                    if (error.code !== 'sendTimeout')
                        throw error;
                    return false;
                }
            }),
            powerOffRestrictions: ['get', 'hasControl'],
        };
        // When powered off, the NEC E505, doesn't respond to power queries but can be powered up using
        // the power on command.  While in this state, the system receives [EDEN PM1] acks from the
        // display usually every 5 seconds, and sometimes multiples of 5 seconds.
        this.power = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(this.makeCommand([
                    0x01,
                    0x30,
                    this.deviceId,
                    0x30,
                    0x41,
                    0x30,
                    0x36,
                    0x02,
                    0x30,
                    0x31,
                    0x44,
                    0x36,
                    0x03, // End of message
                ]), /\x01\x30[\s\S]{5}\x02[\s\S]{15}([\s\S])\x03|(EDEN PM)/i, tags);
                return match[1] ? match[1] === '\x31' : this.state.power.value;
            }),
            set: (power, tags) => __awaiter(this, void 0, void 0, function* () {
                this.state.busy.update(true);
                this.busyTimeout.set(() => this.state.busy.update(false), power ? 13000 : 3000);
                const match = yield this.socket.send(this.makeCommand([
                    0x01,
                    0x30,
                    this.deviceId,
                    0x30,
                    0x41,
                    0x30,
                    0x43,
                    0x02,
                    0x43,
                    0x32,
                    0x30,
                    0x33,
                    0x44,
                    0x36,
                    0x30,
                    0x30,
                    0x30,
                    power ? 0x31 : 0x34,
                    0x03, // ETX end message
                ]), /\x01\x30[\s\S]{5}\x02[\s\S]{11}([\s\S])\x03/i, tags);
                this.framework.suspend(power ? 8000 : 2000);
                return match[1] ? match[1] === '\x31' : this.state.power.value;
            }),
        };
        this.videoInput = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(this.makeCommand([
                    0x01,
                    0x30,
                    this.deviceId,
                    0x30,
                    0x43,
                    0x30,
                    0x36,
                    0x02,
                    0x30,
                    0x30,
                    0x36,
                    0x30,
                    0x03, // ETX end of message
                ]), /\x010[\s\S]{5}\x02[\s\S]{4}60[\s\S]{8}([\s\S][\s\S])\x03/i, tags);
                // match[1] = '0', match[2] = '1'    -->  1
                // match[1] = '0', match[2] = 'A'    --> 10
                // match[1] = '1', match[2] = '1'    --> 17
                // Convert to decimal and remove any leading zeros
                return parseInt(match[1], 16).toString();
            }),
            set: (code, tags) => __awaiter(this, void 0, void 0, function* () {
                // Convert input code to hex string and then to ascii bytes
                const inputCodes = this.createAsciiBytes(code, 4);
                this.state.busy.update(true);
                this.busyTimeout.set(() => this.state.busy.update(false), 4000);
                const match = yield this.socket.send(this.makeCommand([
                    0x01,
                    0x30,
                    this.deviceId,
                    0x30,
                    0x45,
                    0x30,
                    0x41,
                    0x02,
                    0x30,
                    0x30,
                    0x36,
                    0x30,
                    ...inputCodes,
                    0x03, // ETX end message
                ]), /\x01\x30[\s\S]{5}\x02[\s\S]{4}\x36\x30[\s\S]{6}([\s\S]{4})\x03/, tags, { sendTimeoutMs: 3000 });
                // convert to decimal and remove any leading zeros
                return parseInt(match[1], 16).toString();
            }),
            powerOffRestrictions: ['get', 'set'],
        };
        this.volume = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(this.makeCommand([
                    0x01,
                    0x30,
                    this.deviceId,
                    0x30,
                    0x43,
                    0x30,
                    0x36,
                    0x02,
                    0x30,
                    0x30,
                    0x36,
                    0x32,
                    0x03, // ETX end message
                ]), /\x01\x30[\s\S]{5}\x02[\s\S]{4}\x36\x32[\s\S]{8}([\s\S]{2})\x03/i, tags);
                return parseInt(match[1], 16);
            }),
            set: (volume, tags) => __awaiter(this, void 0, void 0, function* () {
                // Convert volume to two ascii digits, e.g., 18d = 0x12 -> 0x31 0x32, 0x64 -> 0x36 0x34
                const [value1, value2] = this.createAsciiBytes(volume.toString());
                const match = yield this.socket.send(this.makeCommand([
                    0x01,
                    0x30,
                    this.deviceId,
                    0x30,
                    0x45,
                    0x30,
                    0x41,
                    0x02,
                    0x30,
                    0x30,
                    0x36,
                    0x32,
                    0x30,
                    0x30,
                    value1,
                    value2,
                    0x03, // ETX end message
                ]), /\x01\x30[\s\S]{5}\x02[\s\S]{4}\x36\x32[\s\S]{8}([\s\S])([\s\S])\x03/, tags);
                return match[1] ? volume : this.state.volume.value;
            }),
            range: { max: 100, min: 0, round: 1 },
            powerOffRestrictions: ['get', 'set'],
        };
        this.volumeMute = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(this.makeCommand([
                    0x01,
                    0x30,
                    this.deviceId,
                    0x30,
                    0x43,
                    0x30,
                    0x36,
                    0x02,
                    0x30,
                    0x30,
                    0x38,
                    0x44,
                    0x03, // ETX - end of message
                ]), /\x01\x30[\s\S]{5}\x02[\s\S]{4}\x38\x44[\s\S]{9}([\s\S])\x03/i, tags);
                return match[1] === '\x31';
            }),
            set: (mute, tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(this.makeCommand([
                    0x01,
                    0x30,
                    this.deviceId,
                    0x30,
                    0x45,
                    0x30,
                    0x41,
                    0x02,
                    0x30,
                    0x30,
                    0x38,
                    0x44,
                    0x30,
                    0x30,
                    0x30,
                    mute ? 0x31 : 0x30,
                    0x03, // ETX end message
                ]), /\x01\x30[\s\S]{5}\x02[\s\S]{4}\x38\x44[\s\S]{9}([\s\S])\x03/i, tags);
                return match[1] === '\x31';
            }),
        };
        // polling
        this.polls = [
            {
                intervalMs: 4000,
                controls: ['power', 'videoInput', 'volume', 'volumeMute', 'displayBlank'],
            },
        ];
    }
    // phase callbacks
    onConfigure(config) {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
        });
    }
    onDisconnect() {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
        });
    }
    onInitialize(credentials) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield CommandSocketInitializer.initialize(this.socket, this.initializerSteps, credentials);
        });
    }
    // other class methods
    // Converts the decimal source code value to two ascii digits that represent
    // the hex values as follows:
    //   1 = 0x01 => [ 0x30, 0x31 ]
    //  17 = 0x11 => [ 0x31, 0x31 ]
    // 130 = 0x82 => [ 0x38, 0x32 ]
    // 200 = 0xC8 => [ 0x43, 0x38 ]
    createAsciiBytes(input, len = 2) {
        const values = [];
        const codeValue = parseInt(input).toString(16).padStart(len, '0');
        for (let i = 0; i < codeValue.length; i++) {
            const value = parseInt(codeValue.substring(i, i + 1), 16);
            values.push(value < 10 ? 0x30 + value : 0x41 + value - 10);
        }
        if (debug)
            this.log.debug(`${input} = 0x${parseInt(input).toString(16).padStart(2, '0')} => ${JSON.stringify(values.map(el => `0x${el.toString(16)}`))}`);
        return values;
    }
    makeCommand(data) {
        let checksum = 0;
        const bytes = data;
        // Compute the checksum.
        for (let i = 1; i < bytes.length; i++) {
            checksum ^= bytes[i];
        }
        bytes.push(checksum & 0x00ff); // Add the checksum to the command.
        return AsciiText.decode(bytes);
    }
    // Convert firmware query result into a human readable string.
    // '012E0203044142' -> '1.234AB'
    parseFirmwareVersion(input) {
        const length = input.length;
        const data = [];
        for (let i = 0; i < length; i += 2) {
            const chars = input.substring(i, i + 2);
            let int = parseInt(chars, 16);
            if (int < 10)
                int += 0x30;
            data.push(String.fromCharCode(int));
        }
        return data.join('');
    }
}
// static properties
NecMultisync.driverType = 'socketDriver';
NecMultisync.key = '-KHGvnz4v1sLsuUjJEDp';
NecMultisync.baseSocketOptions = {
    commandExtractor: {
        terminators: ['\r'],
    },
    sendTerminator: '\r',
    isBinary: true,
};
NecMultisync.socketOptions = [
    Object.assign(Object.assign({}, NecMultisync.baseSocketOptions), { port: 7142 }),
    Object.assign(Object.assign({}, NecMultisync.baseSocketOptions), { port: 4999 }),
];
